import React, { ReactNode, useRef } from 'react';
import { FormData, Mode, NavItemType, Props, Student } from './addStudent.type';
import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import StudentBasicForm from './form/basic/StudentBasicForm';
import { FormikProps } from 'formik';
import { BasicFormValues } from './form/basic/studentBasic.type';
import { Col, Nav, Row, Tab } from 'react-bootstrap';
import { navItems } from './config';
import classNames from 'classnames';
import FeatherIcon from 'feather-icons-react';
import StudentAcademicForm from './form/academic/StudentAcademicForm';
import { AcademicFormValues } from './form/academic/studentAcademicForm.type';
import StudentAddressForm from './form/address/StudentAddressForm';
import { AddressFormValues } from './form/address/studentAddressForm.type';
import {
  useCreateCandidateMutation,
  useFindIndividualCandidateQuery,
  useUpdateCandidateMutation
} from 'service/institutionQuery/institutionQuery';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

const NavItem = ({ item, isLast }: { item: NavItemType; isLast?: boolean }) => {
  return (
    <Nav.Link
      eventKey={item.eventKey}
      className={classNames(
        'text-center text-sm-start cursor-pointer outline-none d-sm-flex align-items-sm-center',
        {
          'border-bottom-sm border-end border-end-sm-0': !isLast
        }
      )}
    >
      <FeatherIcon icon={item.icon} size={16} className="me-sm-2 nav-icons" />
      <span className="d-none d-sm-inline">{item.label}</span>
    </Nav.Link>
  );
};

const titleMap: Record<Mode, string> = {
  add: 'Add a student',
  edit: 'Edit a student',
  view: 'Student'
};

const descMap: Record<Mode, string> = {
  add: 'Enter details to add a new student.',
  edit: 'Change details to update a student.',
  view: 'View student'
};

function AddStudent({ mode, id }: Props) {
  const [createCandidate, { isLoading }] = useCreateCandidateMutation();
  const [updateCandidate, { isLoading: updateCandidateLoader }] =
    useUpdateCandidateMutation();
  const { data, isLoading: candidateLoader } = useFindIndividualCandidateQuery(
    id,
    {
      skip: Boolean(mode === 'add' || !id)
    }
  );

  const navigate = useNavigate();

  const breadcrumbItemsMap: Record<Mode, PageBreadcrumbItem[]> = {
    add: [
      {
        label: 'Students',
        url: '/hub/students'
      },
      {
        label: 'Add student',
        url: '/hub/students/add',
        active: true
      }
    ],
    edit: [
      {
        label: 'Students',
        url: '/hub/students'
      },
      {
        label: 'Edit student',
        url: `/hub/students/edit/${id}`,
        active: true
      }
    ],
    view: [
      {
        label: 'Students',
        url: '/hub/students'
      },
      {
        label: 'View student',
        url: `/hub/students/view/${id}`,
        active: true
      }
    ]
  };

  const basicFormRef = useRef<FormikProps<BasicFormValues> | null>(null);
  const academicFormRef = useRef<FormikProps<AcademicFormValues> | null>(null);
  const addressFormRef = useRef<FormikProps<AddressFormValues> | null>(null);

  const handleSubmit = () => {
    Promise.all([
      basicFormRef.current?.submitForm(),
      academicFormRef.current?.submitForm(),
      addressFormRef.current?.submitForm()
    ]).then(formReferance => {
      const form = formReferance as FormData;
      if (form.every(values => values && typeof values === 'object')) {
        const formData = form.reduce((acc, item) => ({ ...acc, ...item }), {});
        createCandidate(formData)
          .unwrap()
          .then(() => {
            basicFormRef.current?.resetForm();
            academicFormRef.current?.resetForm();
            addressFormRef.current?.resetForm();
          })
          .catch(console.error);
      }
    });
  };

  const handleUpdate = () => {
    Promise.all([
      basicFormRef.current?.submitForm(),
      academicFormRef.current?.submitForm(),
      addressFormRef.current?.submitForm()
    ]).then(formReferance => {
      const form = formReferance as FormData;
      if (form.every(values => values && typeof values === 'object')) {
        const formData = form.reduce((acc, item) => ({ ...acc, ...item }), {});
        updateCandidate({ id, ...formData })
          .unwrap()
          .then(() => {
            navigate('/hub/students');
          })
          .catch(console.error);
      }
    });
  };

  const actionButtonMap: Record<Mode, ReactNode> = {
    add: (
      <Button
        variant="primary"
        onClick={handleSubmit}
        loading={isLoading}
        loadingPosition="start"
      >
        Publish student
      </Button>
    ),
    edit: (
      <Button
        variant="primary"
        onClick={handleUpdate}
        loading={updateCandidateLoader}
        loadingPosition="start"
      >
        Update
      </Button>
    ),
    view: <></>
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItemsMap[mode]} />
      <section className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">{titleMap[mode]}</h2>
            <h5 className="text-body-tertiary fw-semibold">{descMap[mode]}</h5>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button variant="phoenix-secondary" as={Link} to="/hub/students">
              Back
            </Button>
            {actionButtonMap[mode]}
          </div>
        </div>
        {candidateLoader ? (
          <div className="d-flex justify-content-center">
            <Spinner animation="border" role="status" className="me-2">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <StudentBasicForm
                innerRef={basicFormRef}
                data={data as Student}
                isReadOnly={mode === 'view'}
              />
            </div>
            <div>
              <h4 className="mb-3">Additional Information</h4>
              <Tab.Container id="left-tabs-example" defaultActiveKey="academic">
                <Row className="g-0 border-top border-bottom">
                  <Col xs={12} sm={4}>
                    <Nav className="flex-sm-column border-bottom border-bottom-sm-0 border-end-sm fs-9 vertical-tab h-100 justify-content-between">
                      {navItems.map((item, index) => (
                        <NavItem
                          key={item.label}
                          item={item}
                          isLast={index === navItems.length - 1}
                        />
                      ))}
                    </Nav>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Tab.Content className="py-3 ps-sm-4 h-100">
                      <Tab.Pane eventKey="academic" className="h-100">
                        <h5 className="mb-3 text-body-highlight">
                          Academic Information
                        </h5>
                        <StudentAcademicForm
                          innerRef={academicFormRef}
                          data={data as Student}
                          isReadOnly={mode === 'view'}
                        />
                      </Tab.Pane>
                      <Tab.Pane eventKey="address" className="h-100">
                        <h5 className="mb-3 text-body-highlight">
                          Address Information
                        </h5>
                        <StudentAddressForm
                          innerRef={addressFormRef}
                          data={data as Student}
                          isReadOnly={mode === 'view'}
                        />
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default AddStudent;
